import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ComponentLoader from '@/components/common/Loaders/ComponentLoader';
import Modal from '@/components/common/Modal/Modal';
import ProfilePicture from '@/components/common/ProfilePicture/ProfilePicture';
import { NewRequestWhere } from '@/domain/newRequest';
import { AbsenceWithTypeData } from '@/domain/requestsPage';
import { MEDIA_QUERIES_BREAKPOINTS } from '@/globalStyles';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import EditRequest from '@/pages/Requests/common/EditRequest/EditRequest';
import LimitSummary from '@/pages/Requests/common/LimitSummary/LimitSummary';
import RequestCreator from '@/pages/Requests/common/RequestInfo/RequestCreator';
import RequestDetailsContent from '@/pages/Requests/RequestsContent/RequestDetailsModal/RequestDetailsContent';
import useLeadRequestDetailsModalLogic from '@/pages/Requests/RequestsContent/RequestDetailsModal/useRequestDetailsModalLogic';
import { REQUEST_STATUS } from '@/utils/enums/requestsPage';

interface ILeadRequestDetailsModalProps {
    className?: string;
    isModalOpen: boolean;
    isLoading: boolean;
    singleRequestDetails: AbsenceWithTypeData;
    onCloseModal: () => void;
    getRequests: () => void;
    onEditRequest?: () => void;
    onUpdateAbsenceStatus: (
        id: string,
        status: REQUEST_STATUS,
        name: string | undefined,
        isDeletionRequest: boolean
    ) => (event) => void;
    isArchiveTab?: boolean;
    where?: NewRequestWhere;
}

const LeadRequestDetailsModal = ({
    singleRequestDetails,
    isModalOpen,
    isLoading,
    className,
    where,
    onCloseModal,
    getRequests,
    onUpdateAbsenceStatus,
    onEditRequest,
}: ILeadRequestDetailsModalProps) => {
    const { t } = useTranslation();
    const { credentialId, isLead, isAdmin } = useLoggedInUserData();

    const { isEditView, setIsEditView, isActionLoading, rejectDelete, acceptDelete } = useLeadRequestDetailsModalLogic({
        singleRequestDetails,
        where,
        onCloseModal,
        getRequests,
    });

    const { userDto } = singleRequestDetails;

    const handleEditRequest = useCallback(() => {
        onCloseModal();
        onEditRequest?.();
    }, [onCloseModal, onEditRequest]);

    return (
        <Modal open={isModalOpen} title={t('request_types_edit_step_one_title')} onClose={onCloseModal}>
            <ComponentLoader isLoading={isLoading || isActionLoading}>
                <div className={className}>
                    {(isLead || isAdmin) && (
                        <RequestCreator
                            profilePictureUrl={userDto?.profilePictureUrl}
                            firstName={userDto?.firstName ?? ''}
                            lastName={userDto?.lastName ?? ''}
                            email={userDto?.email}
                        />
                    )}

                    {isEditView ? (
                        <EditRequest
                            singleRequestDetails={singleRequestDetails}
                            onCancel={() => setIsEditView(false)}
                            onCloseModal={handleEditRequest}
                            getRequests={getRequests}
                        />
                    ) : (
                        <RequestDetailsContent
                            singleRequestDetails={singleRequestDetails}
                            credentialId={credentialId}
                            onEditClick={() => setIsEditView(true)}
                            onCloseModal={onCloseModal}
                            getRequests={getRequests}
                            rejectDelete={rejectDelete}
                            acceptDelete={acceptDelete}
                            onUpdateAbsenceStatus={onUpdateAbsenceStatus}
                        />
                    )}
                </div>
            </ComponentLoader>
        </Modal>
    );
};

const Memoized = memo(LeadRequestDetailsModal);
const Styled = styled(Memoized)`
    width: 34rem;
    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.tablet}px) {
        width: auto;
    }

    ${LimitSummary} {
        margin: 1.25rem 0;
    }

    ${ProfilePicture} {
        margin-right: 1rem;
    }
`;

export default Styled;
